// src/components/Eventos/ResultModal.js

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  IconButton,
  Paper,
  useMediaQuery,
  useTheme,
  Divider,
  Chip,
  Stack,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import AirIcon from "@mui/icons-material/Air";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonIcon from "@mui/icons-material/Person";
import NumericKeyboard from "./NumericKeyboard";
import { 
  parseTime,
  formatWindInput,
  formatWindForDisplay,
  isValidWindFormat
} from "../../utils/ResultUtils";
import { formatByPrecision } from "../../utils/inputFormatter";

const ResultModal = ({
  open,
  onClose,
  participant,
  eventType,
  isTrackEvent,
  onSave,
  onSaveWind,
  onDelete,
  windEnabled,
  initialValues,
  attemptNumber,
  precision,
  heatWindMode = false,
  heatKey = "",
  initialWind = "",
  seedMarkMode = false,
  onSaveSeedMark,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  const resultRef = useRef(null);
  const windRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [windValue, setWindValue] = useState("");
  const [activeField, setActiveField] = useState("result");

  const [heatWindInput, setHeatWindInput] = useState(initialWind);
  const athleteName = participant?.fullName ?? "Atleta";
  const showWindField = !isTrackEvent && windEnabled;

  // Inicializar inputs al abrir
  useEffect(() => {
    if (heatWindMode) {
      setHeatWindInput(initialWind ? formatWindForDisplay(initialWind) : '');
    } else if (seedMarkMode) {
      setInputValue(initialValues.seedMark || '');
    } else {
      setInputValue(
        isTrackEvent
          ? initialValues.time || ''
          : initialValues.result || ''
      );
      setWindValue(initialValues.wind ? formatWindForDisplay(initialValues.wind) : '');
    }
    
    if (open) {
      const timer = setTimeout(() => {
        (heatWindMode ? windRef : resultRef).current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [heatWindMode, seedMarkMode, initialWind, initialValues, isTrackEvent, open]);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      (heatWindMode ? windRef : resultRef).current?.focus();
    }, 150);
  }, [open, heatWindMode]);

  const isNM = !heatWindMode && initialValues.result === "NM";

  const formatTrackInput = (input) => {
    let cleaned = input.replace(/[^\d:.]/g, '');
    
    if (cleaned.length > 0) {
      if (cleaned.includes('.') && cleaned.includes(':')) {
        const dotIndex = cleaned.indexOf('.');
        const colonIndex = cleaned.indexOf(':');
        if (colonIndex > dotIndex) {
          cleaned = cleaned.replace(':', '');
        }
      }
      
      if (cleaned.length <= 2) {
        return cleaned;
      } else if (cleaned.includes(':')) {
        const parts = cleaned.split(':');
        if (parts[1].includes('.')) {
          const secParts = parts[1].split('.');
          return parts[0].slice(0, 2) + ':' + secParts[0].slice(0, 2) + '.' + secParts[1].slice(0, 2);
        } else {
          if (parts[1].length > 2) {
            return parts[0].slice(0, 2) + ':' + parts[1].slice(0, 2) + '.' + parts[1].slice(2, 4);
          }
          return parts[0].slice(0, 2) + ':' + parts[1].slice(0, 2);
        }
      } else if (cleaned.includes('.')) {
        const parts = cleaned.split('.');
        if (parts[0].length > 2) {
          return parts[0].slice(0, 2) + ':' + parts[0].slice(2, 4) + '.' + parts[1].slice(0, 2);
        }
        return parts[0].slice(0, 2) + '.' + parts[1].slice(0, 2);
      } else if (cleaned.length <= 4) {
        if (cleaned.length > 2) {
          return cleaned.slice(0, 2) + '.' + cleaned.slice(2);
        }
        return cleaned;
      } else if (cleaned.length <= 6) {
        return cleaned.slice(0, 2) + ':' + cleaned.slice(2, 4) + '.' + cleaned.slice(4);
      } else {
        return cleaned.slice(0, 2) + ':' + cleaned.slice(2, 4) + '.' + cleaned.slice(4, 6);
      }
    }
    return cleaned;
  };

  const formatFieldInput = (input) => {
    // Si es una marca especial de salto vertical, devolverla como está
    if (['O', 'X', '-'].includes(input)) {
      return input;
    }
    
    // Eliminar caracteres no numéricos (excepto punto)
    let cleaned = input.replace(/[^\d.]/g, '');
    
    // Si ya tiene punto, mantener el formato existente
    if (cleaned.includes('.')) {
      const [whole, decimal] = cleaned.split('.');
      return whole + '.' + decimal.slice(0, 2); // Máximo 2 decimales
    }
    
    // Auto-formateo para distancias: insertar punto antes de los últimos 2 dígitos
    if (cleaned.length >= 3) {
      const whole = cleaned.slice(0, -2);
      const decimal = cleaned.slice(-2);
      return whole + '.' + decimal;
    }
    
    // Si tiene 1 o 2 dígitos, devolver sin punto
    return cleaned;
  };

  const handleInput = (ch) => {
    if (activeField === "result") {
      setInputValue((prev) => {
        // Para eventos de campo, aplicar formateo automático de distancias
        if (!isTrackEvent) {
          // Eliminar punto temporal para agregar el nuevo dígito
          const cleanPrev = prev.replace('.', '');
          const newValue = cleanPrev + ch;
          return formatFieldInput(newValue);
        } else {
          // Para eventos de pista, mantener lógica existente
          const newValue = (prev + ch).slice(0, 8);
          return formatTrackInput(newValue);
        }
      });
    } else if (activeField === "wind") {
      setWindValue((prev) => {
        // Para vientos, aplicar formateo automático similar a distancias
        // Si es un signo, mantenerlo; si es número, eliminar punto temporal
        if (ch === '-' || ch === '+') {
          // Si ya hay signo, reemplazarlo
          const withoutSign = prev.replace(/^[+\-]/, '');
          return formatWindInput(ch + withoutSign);
        } else {
          // Para números, eliminar punto temporal y agregar el nuevo dígito
          const cleanPrev = prev.replace(/[+\-]/, '').replace('.', '');
          const sign = prev.startsWith('-') ? '-' : (prev.startsWith('+') ? '+' : '');
          const newValue = formatWindInput(sign + cleanPrev + ch);
          return newValue;
        }
      });
    }
  };
  
  const handleBackspace = () => {
    if (activeField === "result") {
      setInputValue((prev) => {
        if (!isTrackEvent) {
          // Para eventos de campo, eliminar el último dígito y re-formatear
          const cleaned = prev.replace('.', '');
          const shortened = cleaned.slice(0, -1);
          return formatFieldInput(shortened);
        } else {
          // Para eventos de pista, eliminar último carácter normalmente
          return prev.slice(0, -1);
        }
      });
    } else if (activeField === "wind") {
      setWindValue((prev) => {
        // Para vientos, eliminar el último dígito y re-formatear
        const sign = prev.startsWith('-') ? '-' : (prev.startsWith('+') ? '+' : '');
        const withoutSign = prev.replace(/^[+\-]/, '');
        const cleaned = withoutSign.replace('.', '');
        const shortened = cleaned.slice(0, -1);
        
        // Si no quedan dígitos, devolver vacío
        if (!shortened) return '';
        
        return formatWindInput(sign + shortened);
      });
    }
  };
  
  const handleEnter = () => {
    if (!heatWindMode && showWindField && activeField === "result") {
      setActiveField("wind");
    } else {
      handleSave();
    }
  };

  const handleSaveHeatWind = () => {
    if (!heatWindInput || heatWindInput.trim() === '') {
      onSaveWind(heatKey, '');
      return;
    }
    
    const formattedWind = formatWindForDisplay(heatWindInput);
    if (!isValidWindFormat(formattedWind)) {
      alert("Formato de viento inválido. Use ±0.0 hasta ±99.9");
      return;
    }
    onSaveWind(heatKey, formattedWind);
  };

  const handleSave = () => {
    if (heatWindMode) {
      const windToSave = heatWindInput && heatWindInput.trim() !== '' 
        ? formatWindForDisplay(heatWindInput) 
        : '';
      onSave(null, { wind: windToSave });
      return;
    }
  
    if (seedMarkMode) {
      if (participant?.idNumber) {
        onSaveSeedMark(participant.idNumber, inputValue);
      }
      return;
    }
  
    const formattedResult = isTrackEvent
      ? formatByPrecision(inputValue, precision)
      : inputValue;
  
    const formattedWind = showWindField && windValue ? formatWindForDisplay(windValue) : '';
    const rawSeconds = isTrackEvent ? parseTime(formattedResult) : undefined;
  
    const payload = isTrackEvent
      ? {
          time: formattedResult,
          ...(typeof rawSeconds === 'number' && { timeRaw: rawSeconds }),
        }
      : {
          result: formattedResult,
          wind: formattedWind,
        };
  
    if (participant?.idNumber) {
      onSave(participant.idNumber, payload);
    }
  };

  const handleDelete = () => {
    if (!heatWindMode && participant?.idNumber) {
      onDelete(participant.idNumber, attemptNumber);
    }
  };

  const isVerticalFieldEvent = ["HJ1", "PV1"].includes(eventType);
  const isHorizontalFieldEvent = ["LJ1","TJ1","DT1","HT1","SP1","JT1"].includes(eventType);

  const getMainDisplayValue = () => {
    if (heatWindMode) {
      return heatWindInput ? formatWindForDisplay(heatWindInput) : "±0.0";
    }
    
    if (seedMarkMode) {
      if (isTrackEvent) {
        return inputValue ? formatTrackInput(inputValue) : "00:00.00";
      } else {
        return inputValue ? formatFieldInput(inputValue) : "0.00";
      }
    }
    
    if (activeField === "result") {
      if (isTrackEvent) {
        return inputValue ? formatTrackInput(inputValue) : "00:00.00";
      } else {
        return inputValue ? formatFieldInput(inputValue) : "0.00";
      }
    } else {
      return windValue ? formatWindForDisplay(windValue) : "±0.0";
    }
  };

  const getDisplayUnit = () => {
    if (heatWindMode || activeField === "wind") return "m/s";
    if (isTrackEvent) return "";
    return "m";
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isMobileOrTablet ? "sm" : "md"}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
          backdropFilter: "blur(20px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          m: isMobile ? 0 : 1,
        }
      }}
    >
      {/* Header modernizado */}
      <DialogTitle 
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: theme.palette.primary.contrastText,
          p: isMobileOrTablet ? 1.5 : 2,
          position: "relative",
          borderBottom: "none",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={isMobileOrTablet ? 1.5 : 2}>
          <Box
            sx={{
              p: isMobileOrTablet ? 0.8 : 1,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
            }}
          >
            {heatWindMode ? <AirIcon sx={{ fontSize: isMobileOrTablet ? "1.2rem" : "1.5rem" }} /> : <PersonIcon sx={{ fontSize: isMobileOrTablet ? "1.2rem" : "1.5rem" }} />}
          </Box>
          <Box flexGrow={1}>
            <Typography variant={isMobileOrTablet ? "h6" : "h5"} sx={{ fontWeight: 700, mb: 0.5, fontSize: isMobileOrTablet ? "1.1rem" : "1.5rem" }}>
              {heatWindMode ? `Viento Heat ${heatKey}` : athleteName}
            </Typography>
            {!heatWindMode && participant?.idNumber && (
              <Chip
                label={`Número: ${participant.idNumber}`}
                size="small"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "inherit",
                  fontWeight: 600,
                  fontSize: isMobileOrTablet ? "0.7rem" : "0.75rem"
                }}
              />
            )}
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: "inherit",
              backgroundColor: "rgba(255,255,255,0.1)",
              width: isMobileOrTablet ? 36 : 40,
              height: isMobileOrTablet ? 36 : 40,
              '&:hover': { backgroundColor: "rgba(255,255,255,0.2)" }
            }}
          >
            <CloseIcon sx={{ fontSize: isMobileOrTablet ? "1.2rem" : "1.5rem" }} />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Content area optimizado */}
      <DialogContent sx={{ p: 0, height: "100%" }}>
        <Grid container sx={{ height: "100%", minHeight: isMobile ? "auto" : "500px" }}>
          {/* Left side - Display y campos */}
          <Grid item xs={12} md={7} sx={{ p: isMobile ? 1.5 : isMobileOrTablet ? 2 : 3 }}>
            {/* Display principal más moderno */}
            <Card
              elevation={0}
              sx={{
                mb: isMobile ? 1 : isMobileOrTablet ? 1.5 : 2,
                background: activeField === "result" 
                  ? `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`
                  : `linear-gradient(135deg, ${theme.palette.info.light} 0%, ${theme.palette.info.main} 100%)`,
                borderRadius: isMobileOrTablet ? 2 : 3,
                border: `2px solid ${
                  activeField === "result" 
                    ? theme.palette.success.main 
                    : theme.palette.info.main
                }`,
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateY(0)",
                '&:hover': {
                  transform: isMobileOrTablet ? "none" : "translateY(-2px)",
                  boxShadow: isMobileOrTablet ? "none" : `0 12px 24px ${
                    activeField === "result" 
                      ? theme.palette.success.main + "40"
                      : theme.palette.info.main + "40"
                  }`
                }
              }}
            >
              <CardContent sx={{ p: isMobile ? 1.2 : isMobileOrTablet ? 1.5 : 2, textAlign: "center" }}>
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 1 }}>
                  <Typography 
                    variant={isMobileOrTablet ? "h4" : "h3"}
                    sx={{ 
                      fontWeight: 900, 
                      color: "white",
                      fontFamily: "monospace",
                      letterSpacing: isMobileOrTablet ? 1 : 2,
                      textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      fontSize: isMobile ? "1.5rem" : isMobileOrTablet ? "1.8rem" : "3rem"
                    }}
                  >
                    {getMainDisplayValue()}
                  </Typography>
                  {getDisplayUnit() && (
                    <Typography 
                      variant={isMobile ? "body1" : isMobileOrTablet ? "h6" : "h5"}
                      sx={{ 
                        color: "rgba(255,255,255,0.9)",
                        fontWeight: 600,
                        fontSize: isMobile ? "0.9rem" : isMobileOrTablet ? "1.1rem" : "1.5rem"
                      }}
                    >
                      {getDisplayUnit()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Input fields más compactos */}
            <Stack spacing={isMobile ? 0.8 : isMobileOrTablet ? 1 : 1.5}>
              {heatWindMode ? (
                <TextField
                  inputRef={windRef}
                  fullWidth
                  label="Viento del Heat"
                  value={heatWindInput}
                  onChange={e => {
                    // Para vientos, eliminar puntos y re-formatear con auto-formateo
                    const input = e.target.value;
                    const sign = input.startsWith('-') ? '-' : (input.startsWith('+') ? '+' : '');
                    const cleanedInput = input.replace(/[^\d]/g, '');
                    const formatted = formatWindInput(sign + cleanedInput);
                    setHeatWindInput(formatted);
                  }}
                  error={heatWindInput && heatWindInput.trim() !== '' && !isValidWindFormat(formatWindForDisplay(heatWindInput))}
                  helperText={heatWindInput && heatWindInput.trim() !== '' && !isValidWindFormat(formatWindForDisplay(heatWindInput)) ? "Formato: ±0.0" : ""}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      backdropFilter: "blur(10px)",
                    }
                  }}
                  onKeyDown={e => {
                    if (/[0-9]/.test(e.key)) {
                      e.preventDefault();
                      // Para vientos, eliminar punto temporal y agregar nuevo dígito
                      const sign = heatWindInput.startsWith('-') ? '-' : (heatWindInput.startsWith('+') ? '+' : '');
                      const cleanPrev = heatWindInput.replace(/[+\-]/, '').replace('.', '');
                      const newValue = formatWindInput(sign + cleanPrev + e.key);
                      setHeatWindInput(newValue);
                    }
                    else if (/[\-+]/.test(e.key)) {
                      e.preventDefault();
                      // Cambiar o agregar signo
                      const withoutSign = heatWindInput.replace(/^[+\-]/, '');
                      const newValue = formatWindInput(e.key + withoutSign);
                      setHeatWindInput(newValue);
                    }
                    else if (e.key === "Backspace") {
                      e.preventDefault();
                      // Para vientos, eliminar el último dígito y re-formatear
                      const sign = heatWindInput.startsWith('-') ? '-' : (heatWindInput.startsWith('+') ? '+' : '');
                      const withoutSign = heatWindInput.replace(/^[+\-]/, '');
                      const cleaned = withoutSign.replace('.', '');
                      const shortened = cleaned.slice(0, -1);
                      
                      if (!shortened) {
                        setHeatWindInput('');
                      } else {
                        setHeatWindInput(formatWindInput(sign + shortened));
                      }
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      handleSaveHeatWind();
                    }
                  }}
                />
              ) : seedMarkMode ? (
                <TextField
                  inputRef={resultRef}
                  fullWidth
                  label="Marca de Siembra"
                  placeholder={isTrackEvent ? "00:00.00" : "0.00"}
                  value={inputValue}
                  onChange={e => {
                    if (!isTrackEvent) {
                      // Para eventos de campo, eliminar puntos y re-formatear
                      const input = e.target.value.replace(/[^\d]/g, '');
                      const formatted = formatFieldInput(input);
                      setInputValue(formatted);
                    } else {
                      // Para eventos de pista, usar formateo existente
                      const newValue = formatTrackInput(e.target.value);
                      setInputValue(newValue);
                    }
                  }}
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      backdropFilter: "blur(10px)",
                    }
                  }}
                  onKeyDown={e => {
                    if (isTrackEvent && /[0-9.:]/.test(e.key)) { 
                      e.preventDefault(); 
                      const cleaned = inputValue.replace(/[^\d]/g, '');
                      const newValue = formatTrackInput(cleaned + e.key);
                      setInputValue(newValue);
                    }
                    else if (!isTrackEvent && /[0-9]/.test(e.key)) {
                      e.preventDefault();
                      const cleaned = inputValue.replace('.', '');
                      const newValue = formatFieldInput(cleaned + e.key);
                      setInputValue(newValue);
                    }
                    else if (e.key === "Backspace") { 
                      e.preventDefault(); 
                      if (!isTrackEvent) {
                        const cleaned = inputValue.replace('.', '');
                        const shortened = cleaned.slice(0, -1);
                        setInputValue(formatFieldInput(shortened));
                      } else {
                        setInputValue(prev => prev.slice(0, -1));
                      }
                    }
                    else if (e.key === "Enter") { 
                      e.preventDefault(); 
                      handleSave(); 
                    }
                  }}
                />
              ) : (
                <>
                  <TextField
                    inputRef={resultRef}
                    fullWidth
                    value={inputValue}
                    label={isTrackEvent ? "Tiempo" : "Resultado"}
                    placeholder={isTrackEvent ? "00:00.00" : "0.00"}
                    disabled={isNM}
                    size="small"
                    onFocus={() => setActiveField("result")}
                    onChange={(e) => {
                      if (!isTrackEvent) {
                        // Para eventos de campo, eliminar puntos y re-formatear
                        const input = e.target.value.replace(/[^\d]/g, '');
                        const formatted = formatFieldInput(input);
                        setInputValue(formatted);
                      } else {
                        // Para eventos de pista, usar formateo existente
                        const newValue = formatTrackInput(e.target.value);
                        setInputValue(newValue);
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: "rgba(255,255,255,0.8)",
                        backdropFilter: "blur(10px)",
                        borderColor: activeField === "result" ? theme.palette.primary.main : "inherit",
                        '&.Mui-focused': {
                          backgroundColor: "rgba(255,255,255,1)",
                        }
                      }
                    }}
                    onKeyDown={e => {
                      if (isTrackEvent && /[0-9.:]/.test(e.key)) { 
                        e.preventDefault(); 
                        handleInput(e.key); 
                      }
                      else if (!isTrackEvent && /[0-9]/.test(e.key)) {
                        e.preventDefault();
                        handleInput(e.key);
                      }
                      else if (e.key === "Backspace") { 
                        e.preventDefault(); 
                        handleBackspace(); 
                      }
                      else if (e.key === "Enter") { 
                        e.preventDefault(); 
                        handleEnter(); 
                      }
                    }}
                  />

                  {showWindField && (
                    <TextField
                      fullWidth
                      value={windValue}
                      label="Viento (m/s)"
                      placeholder="±0.0"
                      disabled={isNM}
                      size="small"
                      inputRef={windRef}
                      onFocus={() => setActiveField("wind")}
                      onChange={(e) => {
                        const newValue = formatWindInput(e.target.value);
                        setWindValue(newValue);
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: "rgba(255,255,255,0.8)",
                          backdropFilter: "blur(10px)",
                          borderColor: activeField === "wind" ? theme.palette.primary.main : "inherit",
                          '&.Mui-focused': {
                            backgroundColor: "rgba(255,255,255,1)",
                          }
                        }
                      }}
                      onKeyDown={e => {
                        if (/[0-9.\-+]/.test(e.key)) { e.preventDefault(); handleInput(e.key); }
                        else if (e.key === "Backspace") { e.preventDefault(); handleBackspace(); }
                        else if (e.key === "Enter") { e.preventDefault(); handleEnter(); }
                      }}
                    />
                  )}
                </>
              )}
            </Stack>

            {/* Botones especiales más compactos */}
            {(isVerticalFieldEvent || isHorizontalFieldEvent) && !isNM && !heatWindMode && !seedMarkMode && (
              <Card 
                elevation={0} 
                sx={{ 
                  mt: isMobile ? 0.8 : isMobileOrTablet ? 1 : 1.5, 
                  borderRadius: 2, 
                  backgroundColor: "rgba(255,255,255,0.6)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                <CardContent sx={{ p: isMobile ? 1 : isMobileOrTablet ? 1.2 : 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.8, fontWeight: 600, fontSize: isMobileOrTablet ? "0.75rem" : "0.8rem" }}>
                    Marcas Especiales
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {isHorizontalFieldEvent && (
                      <Button 
                        variant="contained" 
                        color="error" 
                        size="small"
                        onClick={() => { setInputValue("X"); setActiveField("result"); }}
                        sx={{ 
                          borderRadius: 2,
                          fontSize: "0.7rem",
                          px: 1.2,
                          py: 0.4
                        }}
                      >
                        Foul (X)
                      </Button>
                    )}
                    {isVerticalFieldEvent && (
                      <>
                        <Button 
                          variant="contained" 
                          color="success" 
                          size="small"
                          onClick={() => { setInputValue("O"); setActiveField("result"); }}
                          sx={{ 
                            borderRadius: 2,
                            fontSize: "0.7rem",
                            px: 1.2,
                            py: 0.4
                          }}
                        >
                          Válido (O)
                        </Button>
                        <Button 
                          variant="contained" 
                          color="info" 
                          size="small"
                          onClick={() => { setInputValue("-"); setActiveField("result"); }}
                          sx={{ 
                            borderRadius: 2,
                            fontSize: "0.7rem",
                            px: 1.2,
                            py: 0.4
                          }}
                        >
                          Pasa (-)
                        </Button>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Right side - Teclado numérico */}
          <Grid 
            item 
            xs={12} 
            md={5} 
            sx={{ 
              backgroundColor: theme.palette.grey[50],
              borderLeft: `1px solid ${theme.palette.divider}`,
              p: isMobile ? 1 : isMobileOrTablet ? 1.5 : 3,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: isMobile ? "auto" : "400px"
            }}
          >
            <NumericKeyboard
              onInput={heatWindMode ? (digit => {
                setHeatWindInput(prev => {
                  // Para vientos, eliminar punto temporal y agregar nuevo dígito
                  const sign = prev.startsWith('-') ? '-' : (prev.startsWith('+') ? '+' : '');
                  const cleanPrev = prev.replace(/[+\-]/, '').replace('.', '');
                  return formatWindInput(sign + cleanPrev + digit);
                });
              }) : handleInput}
              onBackspace={heatWindMode ? () => {
                setHeatWindInput(prev => {
                  // Para vientos, eliminar el último dígito y re-formatear
                  const sign = prev.startsWith('-') ? '-' : (prev.startsWith('+') ? '+' : '');
                  const withoutSign = prev.replace(/^[+\-]/, '');
                  const cleaned = withoutSign.replace('.', '');
                  const shortened = cleaned.slice(0, -1);
                  
                  if (!shortened) return '';
                  return formatWindInput(sign + shortened);
                });
              } : handleBackspace}
              onEnter={heatWindMode ? handleSaveHeatWind : handleEnter}
              eventType={eventType}
              isTrackEvent={isTrackEvent}
              disabled={isNM}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* Actions modernizadas */}
      <DialogActions 
        sx={{ 
          p: isMobile ? 1.5 : isMobileOrTablet ? 2 : 3, 
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.grey[25],
        }}
      >
        <Stack direction="row" spacing={isMobile ? 1 : isMobileOrTablet ? 1 : 2} width="100%" justifyContent="space-between">
          <Button 
            onClick={onClose}
            variant="outlined"
            size={isMobile ? "small" : isMobileOrTablet ? "medium" : "large"}
            startIcon={<CloseIcon />}
            sx={{ 
              borderRadius: 2,
              px: isMobile ? 1.5 : isMobileOrTablet ? 2 : 3,
              borderColor: theme.palette.grey[300],
              fontSize: isMobile ? "0.75rem" : isMobileOrTablet ? "0.875rem" : "1rem"
            }}
          >
            Cancelar
          </Button>
          
          <Stack direction="row" spacing={isMobile ? 1 : isMobileOrTablet ? 1 : 2}>
            {!isNM && !heatWindMode && !seedMarkMode && (
              <Button 
                color="error" 
                onClick={handleDelete}
                variant="outlined"
                size={isMobile ? "small" : isMobileOrTablet ? "medium" : "large"}
                startIcon={<DeleteOutlineIcon />}
                sx={{ 
                  borderRadius: 2, 
                  px: isMobile ? 1.5 : isMobileOrTablet ? 2 : 3,
                  fontSize: isMobile ? "0.75rem" : isMobileOrTablet ? "0.875rem" : "1rem"
                }}
              >
                Eliminar
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              size={isMobile ? "small" : isMobileOrTablet ? "medium" : "large"}
              onClick={heatWindMode ? handleSaveHeatWind : handleSave}
              disabled={isNM}
              endIcon={<SendIcon />}
              sx={{ 
                borderRadius: 2,
                px: isMobile ? 2 : isMobileOrTablet ? 3 : 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                fontSize: isMobile ? "0.75rem" : isMobileOrTablet ? "0.875rem" : "1rem",
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                }
              }}
            >
              Guardar
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ResultModal;